import { FC, Fragment, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Route, Routes, useLocation } from 'react-router-dom';

import { Layout } from '../../components/layout';
import i18n from '../../i18n';
import { camelCasify } from '../../utils/navigation';
import { BuildingRoutes } from '../building';
import Energiplan from '../energiplan';
import { NewTiltakForm } from '../energiplan/components/tiltak/newTiltakForm';
import { UpdateTiltakForm } from '../energiplan/components/tiltak/updateTiltakForm';
import MainScreenLayout from '../main';
import { BuildingsTabContent } from '../main/tabs/buildingsTab';
import { ExpertRegistrationTab } from '../main/tabs/expertRegistrationTab';
import { OtherBuildingsTabContent } from '../main/tabs/otherBuildingsTab';
import { SearchTabContent } from '../main/tabs/searchTab';
import { ProtectedRoute } from '../protectedRoute';
import { RegistrationRoutes } from '../registrationV2025';
import { SidebarPageLoader } from '../registrationV2025/components/pageLoader';

import { FeatureTestRoutes } from './featureTestRoutes';
import CenteringContainer from './centeringContainer';

const HomeScreen = lazy(() => import('../home'));
const LoginScreen = lazy(() => import('../login'));
const LogoutScreen = lazy(() => import('../logout'));
const ProfileScreen = lazy(() => import('../profile'));
const DirectLogin = lazy(() => import('../directLogin'));
const PageNotFound = lazy(() => import('../pageNotFound'));
const StatisticsScreen = lazy(() => import('../statistics'));
const CreateBuilding = lazy(() => import('../createBuilding'));
//const ExpertToolsXMLPortfolioScreen = lazy(() => import('../expertToolsXMLPortfolio'));
const EnhetXmlRegistration = lazy(() => import('../expertRegistration/xml/enhet/enhetXmlRegistration'));
const EnhetJsonRegistration = lazy(() => import('../expertRegistration/json/enhet/enhetJsonRegistration'));
const FlerboligXmlRegistration = lazy(() => import('../expertRegistration/xml/flerbolig/flerboligXmlRegistration'));

const FlerboligJsonRegistration = lazy(() => import('../expertRegistration/json/flerbolig/flerboligJsonRegistration'));

export const AppRoutes: FC = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const isGuid = (str: string): boolean => {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
  };

  const defaultTitle = [t('companyName'), t('appName')].join(' ');
  const documentTitle = pathname
    .split('/')
    .reverse()
    .reduce((acc: string[], cur) => (!cur || isGuid(cur) ? acc : [...acc, t(`helmet.${camelCasify(cur)}`)]), [])
    .join(' | ');

  return (
    <Fragment>
      <Helmet
        defaultTitle={defaultTitle}
        htmlAttributes={{ lang: i18n.language }}
        title={documentTitle}
        titleTemplate={`%s | ${defaultTitle}`}
      />
      <Layout>
        <Suspense fallback={<SidebarPageLoader />}>
          <Routes>
            <Route element={<CenteringContainer />}>
              <Route index element={<HomeScreen />} />
              <Route path="login" element={<LoginScreen />} />
              <Route path="logout" element={<LogoutScreen />} />
              <Route path="direct-login" element={<DirectLogin />} />
              <Route path="statistikk" element={<StatisticsScreen />} />
            </Route>
            {/* Routes that require authentication */}
            <Route element={<ProtectedRoute />}>
              <Route path="bygg">
                <Route path="*" element={<BuildingRoutes />} />
                <Route path="" element={<PageNotFound />} />
              </Route>
              <Route path="opprett-bygg" element={<CreateBuilding />} />

              <Route element={<MainScreenLayout />}>
                <Route path="ekspertregistrering" element={<ExpertRegistrationTab />} />
                <Route path="boliger" element={<BuildingsTabContent />} />
                <Route path="sok/*" element={<SearchTabContent />} />
                <Route path="andres-boliger" element={<OtherBuildingsTabContent />} />
              </Route>

              {/* Making portfolio page unavailable until further notice
               <Route path="ekspertregistrering-portefolje/*" element={<ExpertToolsXMLPortfolioScreen />} />
              */}
              <Route element={<CenteringContainer />}>
              <Route path="profil" element={<ProfileScreen />} />
                <Route path="registrer-energimerke/*" element={<RegistrationRoutes />} />
              </Route>

              <Route element={<FeatureTestRoutes />}>
                <Route path="energiplan/:buildingId">
                  <Route index element={<Energiplan />} />
                  <Route path="nytt-tiltak/:energiplanId" element={<NewTiltakForm />} />
                  <Route path="tiltak/:tiltakId" element={<UpdateTiltakForm />} />
                </Route>
              </Route>

              <Route path="ekspertregistrering">
                <Route path="xml">
                  <Route path="enhet/:step?/:registrationId?" element={<EnhetXmlRegistration />} />
                  <Route path="flerbolig/:step?/:flerboligId?" element={<FlerboligXmlRegistration />} />
                </Route>
                <Route path="json">
                  <Route path="enhet/:step?/:registrationId?" element={<EnhetJsonRegistration />} />
                  <Route path="flerbolig/:step?/:flerboligId?" element={<FlerboligJsonRegistration />} />
                </Route>
              </Route>
            </Route>
            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </Suspense>
      </Layout>
    </Fragment>
  );
};
